import { ChevronDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TemplateConfig } from "@/types/email-templates";
import { GenieBranding } from "@/types/ui/branding";
import AgentBrandingPreview from "./AgentBrandingPreview";
import LogoDisplaySection from "./LogoDisplaySection";

interface AgentInfo {
  id: string;
  name: string;
  branding: GenieBranding | null;
}

interface BrandingSettingsProps {
  config: TemplateConfig;
  isOpen: boolean;
  onToggle: () => void;
  onBrandingChange: (key: keyof TemplateConfig["branding"], value: string) => void;
  selectedAgent?: AgentInfo | null;
  onApplyAgentBranding?: () => void;
  isApplyingBranding?: boolean;
  teamBranding?: GenieBranding | null;
  onApplyTeamBranding?: () => void;
}

const BrandingSettings = ({
  config,
  isOpen,
  onToggle,
  onBrandingChange,
  selectedAgent,
  onApplyAgentBranding,
  isApplyingBranding,
  teamBranding,
  onApplyTeamBranding,
}: BrandingSettingsProps) => {
  // Get current colors for display
  const currentColors = [
    { label: "Primary", color: config.colors?.primary },
    { label: "Accent", color: config.colors?.accent },
    { label: "Text", color: config.colors?.text },
    { label: "Background", color: config.colors?.background },
  ].filter((c) => c.color);

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardTitle className="text-lg flex items-center gap-2">
              Branding
              <ChevronDown
                className="ml-auto h-4 w-4 transition-transform"
                style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
              />
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-6">
            {/* Agent Branding Preview - shown when agent is selected */}
            {selectedAgent && onApplyAgentBranding && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Genie Branding</Label>
                <AgentBrandingPreview
                  agentName={selectedAgent.name}
                  branding={selectedAgent.branding}
                  onApply={onApplyAgentBranding}
                  isApplying={isApplyingBranding}
                />
              </div>
            )}

            {/* Team Branding Preview - shown when no agent is selected and team has branding */}
            {teamBranding && onApplyTeamBranding && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Team Branding</Label>
                <AgentBrandingPreview
                  agentName="Team"
                  branding={teamBranding}
                  onApply={onApplyTeamBranding}
                  isApplying={isApplyingBranding}
                />
              </div>
            )}

            {/* Logo Section - visual display */}
            <LogoDisplaySection
              logoUrl={config.branding?.logoUrl}
              onLogoChange={(url) => onBrandingChange("logoUrl", url)}
            />

            {/* Company Name */}
            <div>
              <Label htmlFor="companyName" className="text-sm font-medium">
                Company Name
              </Label>
              <Input
                id="companyName"
                type="text"
                value={config.branding?.companyName || ""}
                onChange={(e) => onBrandingChange("companyName", e.target.value)}
                className="mt-1.5 text-sm"
                placeholder="Help Genie"
              />
            </div>

            {/* Current Colors Preview */}
            {currentColors.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Applied Colors</Label>
                <div className="flex flex-wrap items-center gap-3 bg-gray-50 rounded-lg p-3 border border-gray-200">
                  {currentColors.map((item, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <div
                        className="w-5 h-5 rounded border border-gray-300 shadow-sm"
                        style={{ backgroundColor: item.color }}
                        title={item.color}
                      />
                      <span className="text-xs text-gray-600">{item.label}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Edit colors in the Colors section above
                </p>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default BrandingSettings;
